/**
 * The spectacle round (C0-VFX): lock-on forms (ท่าล็อกเป้า — a continuous combo on the caster with a matching receive on
 * the target: hit, reel, spin, knocked up, slammed to a knee, recover; FX per hit and a big finisher) and each art's
 * ultimate showcase (ท่าพลัง — a long animation of the kind that fits the art: ground-targeted, around the caster, or
 * lock-on). Built on the ink samples, the kind templates and the dragons.
 *
 * Lock-on forms have no move ids yet (C0-DUEL adds the `lockForm` flag and its rules in shared); they are lab samples
 * here, ready to be bound to moves. The showcases replace the ultimates of the sword art (`ns-heaven`), the cloud palm
 * (`cp-heaven`) and the golden dragon palm (`dp9-sky`) in the clone (`registerShowcase`).
 */
import { DRAGON_FORMS } from './dragon';
import { drawOrbit } from './swords';
import { drawGiantPalm } from './palmFx';
import { TEMPLATES } from './kinds';
import { arc, drawCut, drawGather, drawSplats, inkSample, memo, registerShowcase, slashStroke, splats, stroke, type InkSample, type InkScene } from './samples';
import type { Stroke } from './brush';
import { easeOut, span } from './noise';

type Pts = readonly (readonly [number, number])[];

/** A smooth bump: 0 outside [a, b], up to `h` in the middle, held flat over the middle `hold` part. */
const bump = (t: number, a: number, b: number, h: number, hold = 0): number => {
  if (t <= a || t >= b) return 0;
  const u = (t - a) / (b - a);
  const e = (1 - hold) / 2;
  const k = u < e ? u / e : u > 1 - e ? (1 - u) / e : 1;
  return h * Math.sin((Math.min(1, k) * Math.PI) / 2);
};

/** Piecewise-linear time remap through (t, t') points. */
const remap = (t: number, pts: Pts): number => {
  if (t <= pts[0]![0]) return pts[0]![1] + (t - pts[0]![0]);
  for (let i = 1; i < pts.length; i++) {
    const [a, x] = pts[i - 1]!;
    const [b, y] = pts[i]!;
    if (t <= b) return x + ((y - x) * (t - a)) / (b - a || 1);
  }
  const [a, x] = pts[pts.length - 1]!;
  return x + (t - a);
};

// ── lock-on forms ──

const SWORD_HITS = [0.42, 0.72, 1.0, 1.42] as const;
const LOCK_SWORD: InkSample = {
  id: 'lock-sword',
  label: 'ท่าล็อกเป้า: กระบี่ (ฟัน-เสยลอย-แทงกลางอากาศ-ฟันปิดท้าย)',
  skill: null,
  kind: 'attack',
  armed: true,
  motions: [
    [
      'wxLockSword',
      [
        [0, 0], [0.1, 1], [0.25, 2], [0.36, 3], [0.42, 4], [0.5, 5], [0.58, 6], [0.66, 7], [0.72, 8], [0.82, 9],
        [0.94, 10], [1.0, 11], [1.1, 12], [1.28, 13], [1.36, 14], [1.42, 15], [1.5, 16], [1.75, 17], [1.95, 18], [2.1, 19],
      ],
    ],
  ],
  foe: [[0, 'ready'], [0.42, 'hurt']],
  foeMotion: [
    'wxReceive',
    [[0, 0], [0.42, 1], [0.55, 2], [0.72, 3], [0.82, 4], [0.95, 5], [1.02, 6], [1.3, 6.4], [1.42, 7], [1.5, 8], [2.0, 8.4], [2.3, 9], [2.5, 10], [2.6, 11]],
  ],
  foeLift: (t) => bump(t, 0.72, 1.46, 28, 0.2),
  foeSpin: [0.74, 0.98],
  casterLift: (t) => bump(t, 0.84, 1.16, 10),
  casterDash: (t) => bump(t, 0.82, 1.3, 10, 0.3),
  ghosts: [
    [0.36, 0.5],
    [0.66, 1.05],
    [1.34, 1.5],
  ],
  weights: [0, 0, 1, 2],
  bloods: ['down', 'up', 'spit', 'big'],
  hits: SWORD_HITS,
  life: 2.6,
  loop: 3.0,
  release: 0,
  draw(g, t, sc, L) {
    const air = (dy: number): InkScene => ({ ...sc, tdy: sc.tdy - dy });
    // C0-VFX-PREP sword spectacle: six swords circle the caster from the start; one flies into the foe on each of the
    // first three blows (the third at the foe in the air); the rest go into the giant sword of the finisher.
    drawOrbit(g, t, sc, L, 'attack', 0.04, SWORD_HITS.slice(0, 3), [0, 6, 24], SWORD_HITS[3] - 0.45);
    if (L === 'over') {
      drawCut(g, 1, t - SWORD_HITS[0], sc);
      drawCut(g, 2, t - SWORD_HITS[1], sc);
      // The thrust into the foe in the air: a qi blade beam.
      TEMPLATES.beam.draw(g, t - SWORD_HITS[2] + TEMPLATES.beam.hit, air(24), 'over');
    }
    // The finisher: a giant sword dropped on the foe, slamming them down.
    TEMPLATES.giant.draw(g, t - SWORD_HITS[3] + TEMPLATES.giant.hit, { ...sc, tone: 'power', lab: false }, L);
  },
};

const PALM_HITS = [0.32, 0.55, 0.8, 1.3] as const;
const lockPalmTimeline: Pts = [
  [0, 0], [0.12, 1], [0.24, 2], [0.32, 3], [0.44, 4], [0.55, 5], [0.66, 6], [0.8, 7], [0.92, 8], [1.08, 9], [1.2, 10], [1.3, 11], [1.55, 12], [1.8, 13], [2.0, 14],
];
const palmFoe: InkSample['foeMotion'] = [
  'wxReceive',
  [[0, 0], [0.32, 1], [0.44, 2], [0.55, 3], [0.66, 4], [0.8, 5], [0.86, 6], [1.15, 6.4], [1.3, 7], [1.4, 8], [1.9, 8.4], [2.2, 9], [2.4, 10], [2.5, 11]],
];
const uppercut = memo((D, tdy) => ({
  st: {
    pts: [
      [D - 14, tdy - 20],
      [D - 4, tdy - 50],
      [D + 2, tdy - 86],
    ],
    w: 7,
    shape: 'press',
    dry: 0.6,
    seed: 1200,
  } as Stroke,
}));

const LOCK_PALM: InkSample = {
  id: 'lock-palm',
  label: 'ท่าล็อกเป้า: ฝ่ามือ (ซ้าย-ขวา-อัปเปอร์ลอย-ฝ่ามือปิดท้าย)',
  skill: null,
  kind: 'attack',
  armed: false,
  motions: [['wxLockPalm', lockPalmTimeline]],
  foe: [[0, 'ready'], [0.32, 'hurt']],
  foeMotion: palmFoe,
  foeLift: (t) => bump(t, 0.8, 1.36, 30, 0.2),
  foeSpin: [0.56, 0.78],
  casterDash: (t) => bump(t, 0.2, 1.5, 8, 0.6),
  ghosts: [
    [0.28, 0.6],
    [1.2, 1.36],
  ],
  weights: [0, 0, 1, 2],
  bloods: ['spit', 'spit', 'spit', 'spit'],
  hits: PALM_HITS,
  life: 2.5,
  loop: 2.9,
  release: 0,
  draw(g, t, sc, L) {
    const palm = TEMPLATES.palm;
    palm.draw(g, t - PALM_HITS[0] + palm.hit, sc, L);
    palm.draw(g, t - PALM_HITS[1] + palm.hit, { ...sc, tdy: sc.tdy + 6 }, L);
    if (L === 'over') slashStroke(g, uppercut(sc).st, 'attack', sc, easeOut(span(t, PALM_HITS[2] - 0.04, PALM_HITS[2] + 0.04)), span(t, PALM_HITS[2] + 0.1, PALM_HITS[2] + 0.4));
    // The finisher (C0-VFX-PORT, the user's pick): a giant gold qi palm pressing the foe into the ground, with the
    // palm sample's burst under it.
    drawGiantPalm(g, t, sc, L, 'power', PALM_HITS[3]);
    inkSample('palm').draw(g, t - PALM_HITS[3] + 0.34, { ...sc, tone: 'power', blood: false }, L);
  },
};

const LOCK_DRAGON: InkSample = {
  ...LOCK_PALM,
  id: 'lock-dragon',
  label: 'ท่าล็อกเป้า: ฝ่ามือมังกรทอง (มังกร-มังกร-มังกรเสยลอย-มังกรคู่ปิดท้าย)',
  draw(g, t, sc, L) {
    const burst = DRAGON_FORMS['dp9-burst']!;
    burst.draw(g, t - PALM_HITS[0] + burst.hit, sc, L);
    burst.draw(g, t - PALM_HITS[1] + burst.hit, { ...sc, tdy: sc.tdy + 6 }, L);
    if (L === 'over') slashStroke(g, uppercut(sc).st, 'power', sc, easeOut(span(t, PALM_HITS[2] - 0.04, PALM_HITS[2] + 0.04)), span(t, PALM_HITS[2] + 0.1, PALM_HITS[2] + 0.4));
    const lance = DRAGON_FORMS['dp9-lance']!;
    lance.draw(g, t - PALM_HITS[2] + lance.hit * 0.3, { ...sc, tdy: sc.tdy - 30, D: sc.D * 0.5 }, L);
    const dbl = DRAGON_FORMS['dp9-double']!;
    dbl.draw(g, t - PALM_HITS[3] + dbl.hit, sc, L);
  },
};

export const LOCK_FORMS: readonly InkSample[] = [LOCK_SWORD, LOCK_PALM, LOCK_DRAGON];

// ── ultimates' showcases ──

/** Sword art (เก้ากระบี่พิฆาตสวรรค์, ground-targeted): aura, leap, the glyph painted in the air, landing, the great cut. */
const SHOW_SWORD_HIT = 2.3;
const SHOW_SWORD: InkSample = {
  id: 'show-sword',
  label: 'ท่าพลังเต็มรูปแบบ: เก้ากระบี่พิฆาตสวรรค์ (เลือกพื้นที่)',
  skill: 'ns-heaven',
  kind: 'power',
  armed: true,
  motions: [
    [
      'wxUltShow',
      [
        [0, 0], [0.1, 1], [0.22, 2], [0.36, 3], [0.5, 4], [0.62, 5], [0.72, 6], [0.82, 7], [0.92, 8], [1.02, 9],
        [1.12, 10], [1.22, 11], [1.32, 12], [1.42, 13], [1.52, 14], [1.62, 15], [1.74, 16], [1.82, 17], [1.98, 18], [2.18, 19],
        [2.26, 20], [2.3, 21], [2.38, 22], [2.6, 23], [2.75, 24], [2.9, 25], [3.05, 26], [3.2, 27], [3.35, 28], [3.5, 29],
      ],
    ],
  ],
  foe: [[0, 'ready'], [0.7, 'block'], [SHOW_SWORD_HIT, 'hurt'], [3.2, 'ready']],
  foeMotion: [
    'wxReceive',
    [[0, 0], [SHOW_SWORD_HIT - 0.01, 0], [SHOW_SWORD_HIT, 1], [2.4, 2], [2.5, 7], [2.6, 8], [3.1, 8.4], [3.4, 9], [3.6, 11]],
  ],
  casterLift: (t) => bump(t, 0.86, 1.76, 38, 0.55),
  ghosts: [
    [0.8, 1.0],
    [2.2, 2.4],
  ],
  weights: [2],
  bloods: ['big'],
  hits: [SHOW_SWORD_HIT],
  life: 3.6,
  loop: 4.0,
  release: 0,
  draw(g, t, sc, L) {
    // The ultimate sample's own timeline, stretched: gather and ring, the glyph painted while airborne, the cut.
    const tu = remap(t, [
      [0, 0],
      [0.95, 0.6],
      [1.6, 1.25],
      [SHOW_SWORD_HIT, 1.52],
    ]);
    inkSample('ult').draw(g, tu, { ...sc, blood: false }, L);
    // A gold aura spiralling in while it gathers.
    if (L === 'over') drawGather(g, 'power', sc, 0, -46, span(t, 0.1, 0.8), 18, 54, 1210);
  },
};

/** Cloud palm (ฝ่ามือเมฆาครอบฟ้า, around the caster): qi gathered, a leap, palms down onto the ground, rings and clouds rolling out. */
const SHOW_PALM_SLAM = 1.5;
const SHOW_PALM_PUSH = 2.05;
const palmRings = memo(() => ({
  rings: [26, 48, 74, 100].map(
    (r, i): Stroke => ({ pts: arc(0, 0, r, r * 0.44, -2.4 + i * 0.7, -2.4 + i * 0.7 + Math.PI * 1.8, 20), w: 6 - i, shape: 'press', dry: 0.6, body: i % 2 ? 'acc' : 'ink', seed: 1220 + i }),
  ),
  rays: Array.from({ length: 10 }, (_, i): Stroke => {
    const a = (i / 10) * Math.PI * 2 + 0.15;
    return {
      pts: [
        [Math.cos(a) * 16, Math.sin(a) * 7],
        [Math.cos(a) * 92, Math.sin(a) * 40],
      ],
      w: 5,
      shape: 'flick',
      dry: 0.7,
      seed: 1230 + i,
    };
  }),
  clouds: Array.from({ length: 6 }, (_, i): Stroke => {
    const a = (i / 6) * Math.PI * 2 + 0.5;
    const cx = Math.cos(a) * 60;
    const cy = Math.sin(a) * 26 - 6;
    return { pts: arc(cx, cy, 10, 6, Math.PI, Math.PI * 2.6, 10), w: 6, shape: 'swell', dry: 0.5, body: 'acc', seed: 1240 + i };
  }),
}));
const PALM_DUST = splats(1250, 22, -Math.PI / 2, 3.1, 160, 3, 0.4);
const SHOW_PALM: InkSample = {
  id: 'show-palm',
  label: 'ท่าพลังเต็มรูปแบบ: ฝ่ามือเมฆาครอบฟ้า (รอบตัว)',
  skill: 'cp-heaven',
  kind: 'power',
  armed: false,
  motions: [
    [
      'wxUltPalmShow',
      [
        [0, 0], [0.1, 1], [0.2, 2], [0.32, 3], [0.44, 4], [0.58, 5], [0.76, 6], [0.88, 7], [1.0, 8], [1.16, 9], [1.34, 10], [1.5, 11],
        [1.58, 12], [1.7, 13], [1.82, 14], [1.94, 15], [2.05, 16], [2.12, 17], [2.3, 18], [2.5, 19], [2.66, 20], [2.8, 21], [2.95, 22],
      ],
    ],
  ],
  foe: [[0, 'ready'], [SHOW_PALM_SLAM + 0.08, 'hurt']],
  foeMotion: [
    'wxReceive',
    [[0, 0], [SHOW_PALM_SLAM + 0.06, 0], [SHOW_PALM_SLAM + 0.1, 5], [SHOW_PALM_SLAM + 0.16, 6], [1.9, 6.5], [SHOW_PALM_PUSH, 7], [2.15, 8], [2.7, 8.4], [3.0, 9], [3.2, 11]],
  ],
  foeLift: (t) => bump(t, SHOW_PALM_SLAM + 0.1, SHOW_PALM_PUSH + 0.06, 24, 0.3),
  casterLift: (t) => bump(t, 0.92, 1.52, 34, 0.35),
  ghosts: [[0.9, 1.55]],
  weights: [2, 1],
  bloods: ['spit', 'spit'],
  hits: [SHOW_PALM_SLAM + 0.08, SHOW_PALM_PUSH],
  life: 3.2,
  loop: 3.6,
  release: 0,
  draw(g, t, sc, L) {
    const own: InkScene = { ...sc, D: 0, tdy: 0 };
    const R = palmRings(sc);
    const u = t - SHOW_PALM_SLAM;
    if (L === 'ground') {
      R.rings.forEach((st, i) => stroke(g, st, 'power', own, easeOut(span(u, i * 0.06, 0.12 + i * 0.07)), span(u, 0.7 + i * 0.05, 1.4)));
      R.rays.forEach((st, i) => stroke(g, st, 'power', own, easeOut(span(u, 0.02, 0.16)), span(u, 0.5, 1.0), span(u, 0.25 + (i % 3) * 0.03, 0.8)));
      return;
    }
    drawGather(g, 'power', own, 0, -50, span(t, 0.2, 0.86), 18, 50, 1260, 0.7);
    R.clouds.forEach((st, i) => stroke(g, st, 'steel', own, easeOut(span(u, 0.08 + i * 0.03, 0.3 + i * 0.03)), span(u, 0.6, 1.2), span(u, 0.4, 1.0)));
    drawSplats(g, PALM_DUST, 'earth', own, 0, -2, u, 2, 0.5, 300);
    // The last palm sends a wave at the foe.
    inkSample('palm').draw(g, t - SHOW_PALM_PUSH + 0.34, { ...sc, tone: 'power', blood: false }, L);
  },
};

/** Golden dragon palm (มังกรทองเก้าสวรรค์, ground-targeted): the stamp and four dragons, a leap and a roar, the great dragon. */
const SHOW_DRAGON_STOMP = 0.42;
const SHOW_DRAGON_CALL = 1.55;
const SHOW_DRAGON: InkSample = {
  id: 'show-dragon',
  label: 'ท่าพลังเต็มรูปแบบ: มังกรทองเก้าสวรรค์ (เลือกพื้นที่)',
  skill: 'dp9-sky',
  kind: 'power',
  armed: false,
  motions: [
    [
      'wxUltDragonShow',
      [
        [0, 0], [0.1, 1], [0.24, 2], [0.36, 3], [0.42, 4], [0.56, 5], [0.72, 6], [0.9, 7], [1.0, 8], [1.12, 9], [1.3, 10], [1.48, 11],
        [1.6, 12], [1.78, 13], [1.92, 14], [2.04, 15], [2.2, 16], [2.45, 17], [2.65, 18], [2.85, 19], [3.0, 20],
      ],
    ],
  ],
  foe: [[0, 'ready'], [SHOW_DRAGON_CALL + 0.55, 'hurt']],
  foeMotion: [
    'wxReceive',
    [[0, 0], [SHOW_DRAGON_CALL + 0.54, 0], [SHOW_DRAGON_CALL + 0.56, 1], [2.2, 2], [2.3, 7], [2.4, 8], [2.9, 8.4], [3.2, 9], [3.4, 11]],
  ],
  casterLift: (t) => bump(t, 1.0, 1.6, 30, 0.4),
  ghosts: [[0.98, 1.6]],
  weights: [2],
  bloods: ['spit'],
  hits: [SHOW_DRAGON_CALL + 0.55],
  life: 3.4,
  loop: 3.8,
  release: 0,
  draw(g, t, sc, L) {
    DRAGON_FORMS['dp9-stomp']!.draw(g, t - SHOW_DRAGON_STOMP, sc, L);
    DRAGON_FORMS['dp9-sky']!.draw(g, t - SHOW_DRAGON_CALL, sc, L);
    if (L === 'over') drawGather(g, 'power', sc, 0, -100, span(t, 0.7, 1.5), 16, 60, 1270);
  },
};

export const SHOWCASES: readonly InkSample[] = [SHOW_SWORD, SHOW_PALM, SHOW_DRAGON];
for (const s of SHOWCASES) registerShowcase(s);
